<template>
  <action-menu :disabled="disabled" @menu:toggle="isOpen = $event">
    <template #trigger>
      <lf-pill
        size="sm"
        class="font-bold uppercase"
        :color-index="color.index"
        :class="{ 'cursor-pointer': !disabled }"
      >
        <div class="flex space-x-2">
          <span>{{ value }}</span>
          <dropdown-chevron
            v-if="!disabled"
            :is-open="isOpen"
            :is-default-position="false"
            class="mt-1"
            :fill="color.value"
          />
        </div>
      </lf-pill>
    </template>
    <action-menu-item
      v-for="(item, index) in menuItems"
      :key="item.label"
      @click="item.handler(index)"
    >
      {{ item.label }}
    </action-menu-item>
  </action-menu>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { UTIL_COLORS } from "@/helpers/constants";
import { wordsFirstLetterToUpper } from "@/helpers/formatting";
import { usePromiseWrapper } from "@/hooks/common";

import ActionMenuItem from "@/components/ui/ActionMenuItem.vue";
import ActionMenu from "@/components/ui/ActionMenuV2.vue";
import DropdownChevron from "@/components/ui/DropdownChevron.vue";
import LfPill from "@/components/ui/text/LfPill.vue";

import type { MenuItem } from "@/components/ui/ActionMenuWrapperV2.vue";
import type { IApplication } from "@/models/applications";

const {
  modelValue,
  model,
  disabled = false,
} = defineProps<{
  modelValue: boolean;
  disabled: boolean;
  model?: IApplication;
}>();

const { dispatch } = useStore();
const { t } = useI18n();

const isOpen = ref(false);

const color = computed(() => {
  if (disabled) {
    return { index: 33, value: UTIL_COLORS.DISABLED };
  }

  return modelValue
    ? { index: 0, value: UTIL_COLORS.SUCCESS }
    : { index: 2, value: UTIL_COLORS.ERROR };
});

const value = computed(() =>
  modelValue
    ? wordsFirstLetterToUpper(t("COMMON.COMPLETE"))
    : wordsFirstLetterToUpper(t("COMMON.INCOMPLETE"))
);

const menuItems = computed<MenuItem[]>(() => [
  {
    label: wordsFirstLetterToUpper(t("COMMON.INCOMPLETE")),
    handler: (index: number) => selectPlacementMethod(index),
  },
  {
    label: wordsFirstLetterToUpper(t("COMMON.COMPLETE")),
    handler: (index: number) => selectPlacementMethod(index),
  },
]);

const { fetchWrapper: selectPlacementMethod } = usePromiseWrapper(
  async (status: number) => {
    await dispatch("applications/setDiscoveryStatus", {
      status,
      appId: model?.id,
    });
    isOpen.value = false;
  }
);
</script>
